// Local Changelog types mirroring the api-v2 Changelog GraphQL schema.
// NOTE: once the API is deployed and `codegen` re-runs, these can be swapped
// for the generated types in `__generated__/graphql`. Until then the generated
// file does not contain them, so we type the domain/UI layer locally.

export enum ChangelogStatus {
  DRAFT = "DRAFT",
  SCHEDULED = "SCHEDULED",
  PUBLISHED = "PUBLISHED",
  ARCHIVED = "ARCHIVED"
}

export enum ChangelogChannel {
  EMAIL = "EMAIL",
  WHATSAPP = "WHATSAPP",
  PUSH = "PUSH"
}

export type ChangelogTranslation = {
  title: string
  content: string // rich text (HTML)
  shortDescription?: string // plain-text preface for email/notifications
}

export type ChangelogTranslations = Record<string, ChangelogTranslation>

export type CategoryTranslation = { name: string }
export type CategoryTranslations = Record<string, CategoryTranslation>

export type ChannelSendStat = { sent: number; failed: number }

export type ChangelogSendStats = {
  email: ChannelSendStat
  whatsapp: ChannelSendStat
  push: ChannelSendStat
}

// Result of a single S3 upload (cover image).
export type ChangelogMedia = {
  url: string
  type?: string | null
}

export type Changelog = {
  _id: string
  categoryId: string
  coverImageUrl?: string | null
  channels: ChangelogChannel[]
  status: ChangelogStatus
  translations: ChangelogTranslations
  scheduledAt?: string | null
  publishedAt?: string | null
  sendStats?: ChangelogSendStats
  reactionCount?: number
  reactionAvg?: number
  createdAt?: string
  updatedAt?: string
}

export type ChangelogCategory = {
  _id: string
  color: string
  translations: CategoryTranslations
  createdAt?: string
  updatedAt?: string
}

export type ChangelogSenderEmail = { fromAddress: string; fromName: string }

export type ChangelogWhatsappType =
  | "WHATS_APP_BUSINESS"
  | "META_WHATS_APP_BUSINESS"
  | "WHATS_APP_WEB"

export type ChangelogWhatsappParamSource =
  | "title"
  | "shortDescription"
  | "content"
  | "category"
  | "coverImage"
  | "announcementId"
  | "firstName"
  | "lastName"
  | "literal"

export type ChangelogWhatsappParamMap = {
  key: number
  source: ChangelogWhatsappParamSource
}

export type ChangelogWhatsappTemplateEntry = {
  languageCode: string
  templateId: string
  paramMap: ChangelogWhatsappParamMap[]
}

export type ChangelogSenderWhatsapp = {
  type?: ChangelogWhatsappType
  accountId?: string
  templates?: ChangelogWhatsappTemplateEntry[]
  webMessage?: string
  webImageUrl?: string
  webImageType?: string
}

export type ChangelogAudience = "ACTIVE_ONLY" | "ALL"

export type ChangelogSettings = {
  _id?: string
  defaultLanguage: string
  supportedLanguages: string[]
  defaultTimezone: string
  dateFormat: string
  senderEmail: ChangelogSenderEmail
  senderWhatsapp: ChangelogSenderWhatsapp
  senderCustomerId?: string
  recipientAudience?: ChangelogAudience
  includeInactiveCustomers?: boolean
  includeArchivedPersons?: boolean
  updatedAt?: string
}

export type ChangelogPaginate = {
  docs: Changelog[]
  hasNextPage?: boolean
  totalDocs?: number
}

// WhatsApp account / template shapes (subset used by the pickers).
export type WhatsAppBusinessAccount = {
  _id: string
  type: ChangelogWhatsappType
  name?: string
  channelId?: string
  appName?: string
  number?: string
}

export type WhatsAppBusinessTemplate = {
  _id: string
  name: string
  templateId: string
  status?: string
  languageCode?: string
  templateType?: string
  qpienFields?: {
    key: number
    value?: string
    exampleData?: string
    ref: string
  }[]
  type?: ChangelogWhatsappType
}

// ─── Input types ─────────────────────────────────────────────────────────────

export type ListChangelogInput = {
  status?: ChangelogStatus
  categoryId?: string
  search?: string
  pageNumber?: number
  pageSize?: number
}

export type CreateChangelogInput = {
  categoryId: string
  coverImageUrl?: string
  channels: ChangelogChannel[]
  translations: ChangelogTranslations
}

export type UpdateChangelogInput = {
  _id: string
  categoryId?: string
  coverImageUrl?: string
  channels?: ChangelogChannel[]
  translations?: ChangelogTranslations
  notify?: boolean
}

export type PublishChangelogInput = { _id: string; notify?: boolean }

export type ScheduleChangelogInput = { _id: string; scheduledAt: string }

export type CreateChangelogCategoryInput = {
  color: string
  translations: CategoryTranslations
}

export type UpdateChangelogCategoryInput = {
  _id: string
  color?: string
  translations?: CategoryTranslations
}

export type DeleteChangelogCategoryInput = {
  _id: string
  reassignToCategoryId: string
}

export type UpdateChangelogSettingsInput = {
  defaultLanguage?: string
  supportedLanguages?: string[]
  defaultTimezone?: string
  dateFormat?: string
  senderEmail?: ChangelogSenderEmail
  senderWhatsapp?: ChangelogSenderWhatsapp
  senderCustomerId?: string
  recipientAudience?: ChangelogAudience
  includeInactiveCustomers?: boolean
  includeArchivedPersons?: boolean
}

export type ChangelogPrefs = {
  web?: boolean
  mobile?: boolean
  email?: boolean
  whatsapp?: boolean
}

export type ChangelogSubscriberPrefs = ChangelogPrefs

export type ChangelogSubscriber = {
  _id: string
  customer?: { _id: string; companyName?: string } | null
  firstName?: string
  lastName?: string
  email?: string
  phone?: string
  language?: string
  prefs?: ChangelogSubscriberPrefs
  createdAt?: string
}

export type ChangelogSubscriberPaginate = {
  docs: ChangelogSubscriber[]
  totalDocs?: number
  hasNextPage?: boolean
}

export type ListChangelogSubscribersInput = {
  search?: string
  pageNumber?: number
  pageSize?: number
}

// ─── Delivery log ─────────────────────────────────────────────────────────────

export enum ChangelogDeliveryStatus {
  SENT = "SENT",
  FAILED = "FAILED",
  SENDING = "SENDING"
}

export type ChangelogDeliveryChannel = {
  channel: ChangelogChannel
  status: ChangelogDeliveryStatus
  error?: string
  sentAt?: string
}

// announcement/cPerson/customer arrive populated (nested docs) via the Object scalar.
export type ChangelogDeliveryRow = {
  _id: string
  announcement?: {
    _id: string
    translations?: ChangelogTranslations
    status?: string
  } | null
  cPerson?: {
    _id: string
    firstName?: string
    lastName?: string
    email?: string
    phone?: string
    language?: string
  } | null
  customer?: { _id: string; companyName?: string } | null
  channels?: ChangelogDeliveryChannel[]
  readAt?: string | null
  reaction?: number | null
  comment?: string | null
  createdAt?: string
}

export type ChangelogDeliveryPage = {
  docs: ChangelogDeliveryRow[]
  totalDocs?: number
  page?: number
  totalPages?: number
  hasNextPage?: boolean
}

export type ListDeliveriesInput = {
  announcementId?: string
  customerId?: string
  channel?: ChangelogChannel
  status?: ChangelogDeliveryStatus
  read?: boolean
  hasFeedback?: boolean
  pageNumber?: number
  pageSize?: number
}
